import React, { useCallback, useEffect, useRef } from 'react'

import { ColDef } from 'ag-grid-community'

import { useFieldSearch } from 'features/datagrid/hooks/useFieldSearch'
import { RowData } from 'features/datagrid/types'

import SearchInput from 'v2/ui/components/SearchInput'

import { Box } from 'ui/components/Box'

import { useDataGridContext } from './useDataGridContext'

type FieldSearchProps = {
    onFieldFound?: (fieldName: string) => void
}

export const FieldSearch: React.FC<FieldSearchProps> = ({ onFieldFound }) => {
    const searchInputRef = useRef<HTMLInputElement>(null)
    const { gridApi } = useDataGridContext()
    const { searchValue, isVisible, handleSearch, hideSearch, showSearch } = useFieldSearch()

    const handleSearchChange = useCallback(
        (value: string) => {
            handleSearch(value)
            if (!gridApi || !value) {
                return
            }

            // Get all column definitions
            const columnDefs = gridApi.getColumnDefs() as ColDef<RowData>[]
            if (!columnDefs) return

            // Find the first column that matches the search value (case insensitive)
            const matchingColumn = columnDefs.find((col) => {
                const headerName = col.headerName?.toLowerCase()
                const field = col.field?.toLowerCase()
                const searchLower = value.toLowerCase()
                return headerName?.includes(searchLower) || field?.includes(searchLower)
            })

            if (matchingColumn?.field) {
                // Ensure the column is visible and scroll to it
                gridApi.ensureColumnVisible(matchingColumn.field, 'middle')

                // Notify that a field was found
                onFieldFound?.(matchingColumn.field)
            }
        },
        [gridApi, onFieldFound, handleSearch]
    )

    // Handle Command+F / Ctrl+F
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
                e.preventDefault()
                showSearch()
                // Focus the search input after a short delay to ensure it's mounted
                setTimeout(() => {
                    searchInputRef.current?.focus()
                }, 0)
            } else if (e.key === 'Escape' && isVisible) {
                hideSearch()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isVisible, hideSearch, showSearch])

    useEffect(() => {
        const handleShowSearch = (event: CustomEvent<{ searchValue: string }>) => {
            showSearch()
            // Use requestAnimationFrame to ensure the search UI is visible before setting the value
            requestAnimationFrame(() => {
                handleSearch(event.detail.searchValue)
                // Trigger the search change to ensure scrolling
                handleSearchChange(event.detail.searchValue)
            })
        }

        window.addEventListener('show-field-search', handleShowSearch as EventListener)
        return () =>
            window.removeEventListener('show-field-search', handleShowSearch as EventListener)
    }, [showSearch, handleSearch, handleSearchChange])

    if (!isVisible) {
        return null
    }

    return (
        <Box
            p="m"
            style={{
                borderBottom: '1px solid var(--border-color)',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'var(--surface-strongest)',
            }}
        >
            <SearchInput
                ref={searchInputRef}
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search fields... (Press Esc to close)"
                variant="admin"
            />
        </Box>
    )
}
